import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { EnvelopeOpen, Plus, Trash, PencilSimple, Copy } from '@phosphor-icons/react';
import { emailTemplateApi, formatApiError } from '../services/api';

/** Standalone management UI used in Settings. */
const EmailTemplatesManager = () => {
  const [templates, setTemplates] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { id?, name, subject, body }
  const [showEditor, setShowEditor] = useState(false);
  const [saving, setSaving] = useState(false);
  const subjectRef = React.useRef();
  const bodyRef = React.useRef();
  const [activeField, setActiveField] = useState('body');

  const load = async () => {
    try {
      const [tpls, phs] = await Promise.all([
        emailTemplateApi.list(),
        emailTemplateApi.placeholders(),
      ]);
      setTemplates(tpls.data || []);
      setPlaceholders(phs.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => {
    setEditing({ name: '', subject: '', body: '' });
    setShowEditor(true);
  };
  const startEdit = (t) => {
    setEditing({ ...t });
    setShowEditor(true);
  };

  const save = async () => {
    if (!editing.name.trim() || !editing.body.trim()) {
      toast.error('Name und Inhalt erforderlich'); return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        await emailTemplateApi.update(editing.id, {
          name: editing.name, subject: editing.subject, body: editing.body,
        });
        toast.success('Vorlage aktualisiert');
      } else {
        await emailTemplateApi.create({
          name: editing.name, subject: editing.subject, body: editing.body,
        });
        toast.success('Vorlage erstellt');
      }
      setShowEditor(false);
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Vorlage "${t.name}" löschen?`)) return;
    try {
      await emailTemplateApi.delete(t.id);
      toast.success('Vorlage gelöscht');
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const insertPlaceholder = (key) => {
    const token = `{{${key}}}`;
    if (activeField === 'subject') {
      const el = subjectRef.current;
      const pos = el?.selectionStart ?? (editing.subject || '').length;
      const next = (editing.subject || '').slice(0, pos) + token + (editing.subject || '').slice(pos);
      setEditing({ ...editing, subject: next });
      setTimeout(() => el?.focus(), 30);
    } else {
      const el = bodyRef.current;
      const pos = el?.selectionStart ?? (editing.body || '').length;
      const next = (editing.body || '').slice(0, pos) + token + (editing.body || '').slice(pos);
      setEditing({ ...editing, body: next });
      setTimeout(() => el?.focus(), 30);
    }
  };

  const copyPlaceholder = (key) => {
    navigator.clipboard?.writeText(`{{${key}}}`);
    toast.success(`{{${key}}} kopiert`);
  };

  return (
    <div className="card-swiss p-6" data-testid="email-templates-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#E60000]/10 rounded-sm flex items-center justify-center">
            <EnvelopeOpen weight="fill" size={20} className="text-[#E60000]" />
          </div>
          <div>
            <h2 className="text-lg font-bold">E-Mail-Vorlagen</h2>
            <p className="text-sm text-[#525866]">Wiederkehrende Antworten mit Platzhaltern.</p>
          </div>
        </div>
        <Button onClick={startNew} className="btn-primary" data-testid="new-email-template-btn">
          <Plus weight="bold" size={16} className="mr-1" /> Neue Vorlage
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-[#8A94A6]">Lade…</p>
      ) : templates.length === 0 ? (
        <p className="text-sm text-[#8A94A6] italic">Noch keine Vorlagen. Klick „Neue Vorlage", um zu starten.</p>
      ) : (
        <div className="space-y-2">
          {templates.map((t) => (
            <div key={t.id} className="flex items-start gap-3 p-3 bg-white border border-[#E2E4E9] rounded-sm" data-testid={`template-${t.id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[#111418]">{t.name}</p>
                {t.subject && <p className="text-xs text-[#525866] truncate">Betreff: {t.subject}</p>}
                <p className="text-xs text-[#8A94A6] truncate">{(t.body || '').slice(0, 120)}{(t.body || '').length > 120 ? '…' : ''}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => startEdit(t)} data-testid={`edit-tpl-${t.id}`}>
                <PencilSimple size={14} />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => remove(t)} data-testid={`delete-tpl-${t.id}`}>
                <Trash size={14} className="text-red-600" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Placeholder reference */}
      <details className="mt-6 p-3 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm">
        <summary className="text-sm font-semibold cursor-pointer">Verfügbare Platzhalter ({placeholders.length})</summary>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mt-2">
          {placeholders.map((p) => (
            <div key={p.key} className="flex items-center gap-2 text-xs">
              <button type="button" onClick={() => copyPlaceholder(p.key)} className="font-mono text-[#E60000] bg-white border border-[#E2E4E9] px-2 py-0.5 rounded-sm hover:bg-[#E60000]/5">
                {`{{${p.key}}}`}
              </button>
              <span className="text-[#525866]">{p.desc}</span>
            </div>
          ))}
        </div>
      </details>

      {/* Editor Modal */}
      <Dialog open={showEditor} onOpenChange={(v) => { if (!v) { setShowEditor(false); setEditing(null); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Vorlage bearbeiten' : 'Neue Vorlage'}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label className="overline">Name *</Label>
                <Input
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  placeholder="z.B. Angebot senden"
                  data-testid="tpl-name-input"
                />
              </div>
              <div>
                <Label className="overline">Betreff (mit Platzhaltern)</Label>
                <Input
                  ref={subjectRef}
                  onFocus={() => setActiveField('subject')}
                  value={editing.subject || ''}
                  onChange={(e) => setEditing({ ...editing, subject: e.target.value })}
                  placeholder="Ihr Angebot von {{firma_name}}"
                  data-testid="tpl-subject-input"
                />
              </div>
              <div>
                <Label className="overline">Inhalt *</Label>
                <textarea
                  ref={bodyRef}
                  onFocus={() => setActiveField('body')}
                  value={editing.body || ''}
                  onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                  className="input-swiss w-full px-3 py-2 min-h-[200px]"
                  placeholder="{{kunde_anrede}},\n\nvielen Dank für Ihre Anfrage..."
                  data-testid="tpl-body-input"
                />
              </div>
              <div>
                <Label className="overline">Platzhalter einfügen</Label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {placeholders.map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => insertPlaceholder(p.key)}
                      title={p.desc}
                      className="text-xs font-mono px-2 py-1 bg-white border border-[#E2E4E9] rounded-sm hover:bg-[#E60000]/5 hover:border-[#E60000]/30 text-[#E60000]"
                      data-testid={`insert-ph-${p.key}`}
                    >
                      {`{{${p.key}}}`}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-[#8A94A6] mt-1">Tipp: Setze den Cursor zuerst in das Feld, in das der Platzhalter eingefügt werden soll.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditor(false); setEditing(null); }}>Abbrechen</Button>
            <Button onClick={save} disabled={saving} className="btn-primary" data-testid="save-tpl-btn">
              {saving ? 'Speichere…' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailTemplatesManager;

/** Inline "Save as template" mini-modal used in the reply composer */
export const SaveAsTemplateButton = ({ subject, body, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) { toast.error('Name erforderlich'); return; }
    if (!body || !body.trim()) { toast.error('Leerer Inhalt'); return; }
    setSaving(true);
    try {
      await emailTemplateApi.create({ name: name.trim(), subject: subject || '', body });
      toast.success('Als Vorlage gespeichert');
      setOpen(false);
      setName('');
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="save-as-template-btn">
        <Copy size={12} className="mr-1" /> Als Vorlage speichern
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Als Vorlage speichern</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="overline">Name der Vorlage</Label>
            <Input
              value={name} onChange={(e) => setName(e.target.value)}
              placeholder="z.B. Standardantwort"
              data-testid="save-tpl-inline-name"
              onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
              autoFocus
            />
            <p className="text-xs text-[#8A94A6]">Aktueller Betreff + Inhalt werden 1:1 übernommen (inkl. Platzhalter).</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button onClick={submit} disabled={saving} className="btn-primary" data-testid="save-tpl-inline-btn">
              {saving ? 'Speichere…' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
